import { useSnackbar as useNotistackSnackbar, VariantType, SnackbarKey } from 'notistack'

export interface SnackbarOptions {
  variant?: VariantType
  position?: {
    vertical?: 'top' | 'bottom'
    horizontal?: 'left' | 'center' | 'right'
  }
  autoHideDuration?: number
  persist?: boolean
  preventDuplicate?: boolean
  dense?: boolean
  action?: React.ReactNode
}

export const useSnackbar = () => {
  const { enqueueSnackbar, closeSnackbar } = useNotistackSnackbar()

  const showSnackbar = (message: string, options?: SnackbarOptions): SnackbarKey => {
    const {
      variant = 'success',
      position,
      autoHideDuration,
      persist,
      preventDuplicate,
      dense,
      action,
    } = options || {}

    return enqueueSnackbar(message, {
      variant,
      ...(position && {
        anchorOrigin: {
          vertical: position.vertical || 'bottom',
          horizontal: position.horizontal || 'left',
        },
      }),
      ...(autoHideDuration !== undefined && { autoHideDuration }),
      ...(persist !== undefined && { persist }),
      ...(preventDuplicate !== undefined && { preventDuplicate }),
      ...(dense !== undefined && { dense }),
      ...(action && { action }),
    })
  }

  const hideSnackbar = (key?: SnackbarKey) => {
    closeSnackbar(key)
  }

  const showSuccess = (message: string, options?: Omit<SnackbarOptions, 'variant'>) =>
    showSnackbar(message, { ...options, variant: 'success' })

  const showError = (message: string, options?: Omit<SnackbarOptions, 'variant'>) =>
    showSnackbar(message, { ...options, variant: 'error' })

  const showWarning = (message: string, options?: Omit<SnackbarOptions, 'variant'>) =>
    showSnackbar(message, { ...options, variant: 'warning' })

  const showInfo = (message: string, options?: Omit<SnackbarOptions, 'variant'>) =>
    showSnackbar(message, { ...options, variant: 'info' })

  return {
    showSnackbar,
    hideSnackbar,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  }
}
